import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import { Loader } from '@els/els-react--loader';
import { Card } from '@els/els-react--card';
import { useHistory } from 'react-router-dom';
import { tgitems } from '@Constants';
import UserDatabases from '@Services/UserDatabases';
import ResultsList from '@Components/Results/ResultsList';
import { ResultsMap } from '@Containers/ResultsPreview/contexts';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import BlockLabel from '@Containers/ResultsPreview/Components/BlockLabel';
import BlockFeedbacks from '@Containers/ResultsPreview/Components/BlockFeedbacks';
import BlockActionButtons from '@Containers/ResultsPreview/Components/BlockActionButtons';
import ResultsPreviewAIBanner from '@Containers/ResultsPreview/Components/ResultsPreviewAIBanner';
import isAIEnhancedPreviewItem from '@Containers/ResultsPreview/utils';

function GroupSingleDatabase({
  previewItem,
  previewItemIndex,
  showMoreResults,
  bioactivityVisualizationButtonEnabled,
  showStructurePopup,
  copyQuery,
  createAlert,
  redirectToResults,
  cancelQueriesInGroup,
}) {
  const QUERY_CANCELLED_PATH = 'query.cancelled';

  const showDatabaseLabel = !UserDatabases.isRCSDatabase(previewItem.databaseId) && previewItem.fullyLoaded;
  const showProgressLoader = !previewItem.fullyLoaded && !get(previewItem, QUERY_CANCELLED_PATH);
  const showQueryCancelled = get(previewItem, QUERY_CANCELLED_PATH);
  const showViewResults = previewItem.fullyLoaded && previewItem.resultsCount > 0;
  const showPredictionButton = previewItem.fullyLoaded && !get(previewItem, QUERY_CANCELLED_PATH);
  const showBioactivityVisualizationButton =
    bioactivityVisualizationButtonEnabled &&
    previewItem.context === tgitems &&
    previewItem.fullyLoaded &&
    previewItem.resultName &&
    !get(previewItem, QUERY_CANCELLED_PATH);
  const showAIBanner = isAIEnhancedPreviewItem(previewItem);

  const [expanded, setExpanded] = useState(false);
  const history = useHistory();
  const viewResultsButtonRef = useRef(null);
  const previewListRef = useRef(null);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (expanded) {
      showMoreResults(previewItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useDidUpdateEffect(() => {
    if (expanded) {
      previewListRef.current?.focus();
    } else {
      viewResultsButtonRef.current?.focus();
    }
  }, [expanded]);

  const resultsMap = useContext(ResultsMap);
  const results = resultsMap[previewItem.queryId];
  const iconName = get(previewItem, 'title', '')?.toLowerCase().replace(' ', '-');
  return (
    <div
      className={cn('quick-results-preview__item e2e-quick-results-item', {
        'quick-results-preview__item-loading': !previewItem.fullyLoaded,
        'u-els-margin-bottom-2x': showAIBanner,
      })}>
      <div className={`quick-results-preview__item_image quick-results-preview__item_image-${iconName}`} />
      <Card className="quick-results-preview__item_wrapper">
        {showAIBanner && <ResultsPreviewAIBanner />}
        <div className="quick-results-preview__item_controls">
          <BlockLabel previewItem={previewItem} showResultsCount showTitle showDatabaseLabel={showDatabaseLabel} />
          <div className="quick-results-preview__item_controls_wrapper">
            <BlockFeedbacks
              previewItem={previewItem}
              copyQuery={copyQuery}
              createAlert={createAlert}
              showStructurePopup={showStructurePopup}
              showFeedbacks
            />
          </div>
          <BlockActionButtons
            previewItem={previewItem}
            showProgressLoader={showProgressLoader}
            onCancelClick={() => cancelQueriesInGroup(previewItem.groupId)}
            showQueryCancelled={showQueryCancelled}
            showExpandButton={showViewResults}
            expandButtonConfig={{
              label: 'Preview Results',
              e2eName: 'preview-results',
            }}
            expandButtonAttrs={{
              'aria-label': `Preview results from ${previewItem.databaseLabel}`,
            }}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
            showViewResultsButton={showViewResults}
            onViewResultsButtonClick={() => redirectToResults(previewItem, history, previewItemIndex)}
            viewResultsButtonRef={viewResultsButtonRef}
            showPredictionButton={showPredictionButton}
            showBioactivityVisualizationButton={showBioactivityVisualizationButton}
          />
        </div>

        {expanded && (
          <div
            id={previewItem.resultName}
            ref={previewListRef}
            tabIndex={-1}
            className={`quick-results-preview__item_preview u-els-margin-top quick-results-preview__item_preview-${iconName}`}>
            <h3 className="u-els-font-family-bold u-els-margin-bottom-1o2 u-els-font-size-h4">Top 3 results</h3>

            {results ? (
              <ResultsList metadata={previewItem} context={previewItem.context} results={results} size={results.length} hideCheckbox />
            ) : (
              <Loader isSmall isBlocking={false} className="results-preview__progress-loader" />
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

GroupSingleDatabase.propTypes = {
  previewItem: PropTypes.object.isRequired,
  previewItemIndex: PropTypes.number,
  showMoreResults: PropTypes.func.isRequired,
  bioactivityVisualizationButtonEnabled: PropTypes.bool,
  showStructurePopup: PropTypes.func.isRequired,
  copyQuery: PropTypes.func.isRequired,
  createAlert: PropTypes.func.isRequired,
  redirectToResults: PropTypes.func.isRequired,
  cancelQueriesInGroup: PropTypes.func.isRequired,
};

export default GroupSingleDatabase;
